/**
 * Validações de identificadores brasileiros e de e-mail.
 *
 * A formatação (máscaras) fica por conta do `@dionatanbig2be/big2be-ui`
 * (`applyMask` / prop `mask` do `TextInput`); aqui ficam só as regras de
 * validação que a lib não cobre — o `validateMask` dela checa apenas a
 * QUANTIDADE de dígitos, sem dígito verificador nem regras de DDD/celular.
 */

// Subpath `/utils`: helpers puros, SEM a diretiva `'use client'` do barrel —
// este módulo é alcançado a partir de Server Components.
import { removeNonNumbers } from "@dionatanbig2be/big2be-ui/utils";

/** Valida e-mail: algo@dominio.tld, sem espaços. */
export function isValidEmail(value: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());
}

/** Valida telefone BR: fixo (10 dígitos) ou celular (11, começando com 9), DDD sem zero. */
export function isValidPhone(value: string): boolean {
    const digits = removeNonNumbers(value);

    if (digits.length !== 10 && digits.length !== 11) return false;
    if (digits[0] === "0") return false;
    if (digits.length === 11 && digits[2] !== "9") return false;

    return true;
}

function validateCpf(cpf: string): boolean {
    if (/^(\d)\1{10}$/.test(cpf)) return false;

    for (const length of [9, 10]) {
        let sum = 0;
        for (let i = 0; i < length; i++) {
            sum += Number(cpf[i]) * (length + 1 - i);
        }
        const digit = ((sum * 10) % 11) % 10;
        if (digit !== Number(cpf[length])) return false;
    }

    return true;
}

function validateCnpj(cnpj: string): boolean {
    if (/^(\d)\1{13}$/.test(cnpj)) return false;

    const checkDigit = (length: number): number => {
        const weights =
            length === 12
                ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
                : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
        const sum = weights.reduce((acc, weight, i) => acc + Number(cnpj[i]) * weight, 0);
        const rest = sum % 11;
        return rest < 2 ? 0 : 11 - rest;
    };

    return checkDigit(12) === Number(cnpj[12]) && checkDigit(13) === Number(cnpj[13]);
}

/** Valida CPF (11 dígitos) ou CNPJ (14 dígitos) pelo dígito verificador. */
export function isValidCpfOrCnpj(value: string): boolean {
    const digits = removeNonNumbers(value);

    if (digits.length === 11) return validateCpf(digits);
    if (digits.length === 14) return validateCnpj(digits);
    return false;
}

/**
 * Valida CPF (11 dígitos) — sem aceitar CNPJ. Use onde o destino exige pessoa
 * física de verdade (ex.: titular do cartão: o `PaymentService.validateCreditCard`
 * da nest só aceita 11 dígitos).
 */
export function isValidCpf(value: string): boolean {
    const digits = removeNonNumbers(value);
    return digits.length === 11 && validateCpf(digits);
}
